import * as module from 'module';
import * as logger from 'OK/logger';
import ReactInitializer, { type AssetsResources } from 'OK/react/ReactInitializer';
import { getReactLoaderHolder, ReactElementStatus, ReactLoaderHolderHandlerType } from 'OK/react/ReactLoaderHolder';
import type { ReactLoaderHolderHandlerObject } from 'OK/react/ReactLoaderHolder';
import * as Capture from 'OK/capture';


/** Селектор для поиска элемента с react */
const REACT_ELEMENT_SELECTOR = '[data-module="react-loader"]';

const enum LogCodes {
    /**
     * Попытка активировать приложение
     */
    ACTIVATE_ATTEMPT = 'react-loader-activate-attempt',
    /**
     * Приложение успешно активировалось
     */
    ACTIVATE_SUCCESS = 'react-loader-activate-success',
    /**
     * Произошла ошибка при активации приложения
     */
    ACTIVATE_ERROR = 'react-loader-activate-error',
    /**
     * При деактивации приложения сработал ReactInitializer.onAppRenderComplete
     */
    DEACTIVATE_RENDER_COMPLETE = 'react-loader-deactivate-render-complete',
    /**
     * Попытка деактивировать приложение
     */
    DEACTIVATE_ATTEMPT = 'react-loader-deactivate-attempt',
    /**
     * Приложение успешно деактивировалось
     */
    DEACTIVATE_SUCCESS = 'react-loader-deactivate-success',
    /**
     * Произошла ошибка при деактивации приложения
     */
    DEACTIVATE_ERROR = 'react-loader-deactivate-error',
}

export default class ReactLoader {
    private element!: HTMLElement;
    private reactInitializer!: ReactInitializer;

    /**
     * Активирует Реакт-приложение на переданном элементе и вложенные в него другие Реакт-приложения.
     *
     * @example
     * // `html` generated by server, use getAppElement to get element manually by app name.
     * <sc-app id="sc-app-element" data-react="..."></sc-app>
     *
     * // `js`
     * const scAppElement = document.getElementById('sc-app-element');
     * activate(scAppElement).then(() => {
     *   // sc-app already initialized here.
     * });
     */
    public activate(element: HTMLElement): Promise<void | Error> {
        if (module.config().isAwaitChildren) {
            // активация Реакт-приложения вместе с вложенными в него реакт-приложениями
            return this.activateWithChildren(element);
        }

        this.element = element;

        const requiredResources = this.extractRequiredResources(element);
        const dataAttributes = this.extractDataAttributes(element);
        const uiParts = this.extractUIParts(element);

        this.reactInitializer = new ReactInitializer(element, requiredResources, dataAttributes, uiParts);

        return this.reactInitializer.initialize();
    }

    private activateWithChildren(element: HTMLElement): Promise<void | Error> {
        const holder = getReactLoaderHolder(element);

        /**
         * Если элемент имеет статус отличный от начального,
         * значит его уже пытались активировать и повторно этого делать не нужно.
         */
        if (holder.getStatus() !== ReactElementStatus.INITIAL) {
            return Promise.resolve();
        }

        holder.setStatus(ReactElementStatus.PENDING);
        this.element = element;
        const tagName = element.tagName;
        this.log(LogCodes.ACTIVATE_ATTEMPT, tagName);

        const children = getInnerReactBlocks(element);
        const activationChain: Promise<void | Error> =
            // получаем цепочку активаций всех дочерних react-элементов
            this.getOrderedAllReactChildren(
                children,
                (child) => this.activateChild(child),
            );

        return activationChain
            .then(() => {
                const requiredResources = this.extractRequiredResources(element);
                const dataAttributes = this.extractDataAttributes(element);
                const uiParts = this.extractUIParts(element);

                this.reactInitializer = new ReactInitializer(element, requiredResources, dataAttributes, uiParts);

                return this.reactInitializer.initialize();
            })
            .then(() => {
                holder.setStatus(ReactElementStatus.ACTIVATED);
                this.log(LogCodes.ACTIVATE_SUCCESS, tagName);
                // вызываем все зарегистрированные обработчики.
                holder.fireHandlers(ReactLoaderHolderHandlerType.ACTIVATE);
            })
            .catch((error) => {
                holder.setStatus(ReactElementStatus.ERROR);
                this.log(LogCodes.ACTIVATE_ERROR, tagName);
                /**
                 * если какое-то из реакт-приложений не смогло активироваться,
                 * то обязательно вызываем обработчики, чтобы не стопить цепочку активаций.
                 */
                holder.fireHandlers(ReactLoaderHolderHandlerType.ACTIVATE);
                throw error;
            });
    }

    /**
     * Деактивирует реакт-приложение и вложенные в него реакт-приложения
     */
    public deactivate(): Promise<void> | undefined {
        if (module.config().isAwaitChildren) {
            // деактивация Реакт-приложения вместе с вложенными в него реакт-приложениями
            return this.deactivateWithChildren();
        }

        const tagName = this.element.tagName;
        OK.Tracer.log(`Деактивируем: ${tagName}`);
        const reactInitializer = this.reactInitializer;
        ReactInitializer.onAppRenderComplete(this.element).then(function () {
            OK.Tracer.log(`Для ${tagName} сработал onAppRenderComplete`);
            reactInitializer.destroy();
        });
    }

    public deactivateWithChildren(): Promise<void> {
        const holder = getReactLoaderHolder(this.element);

        /**
         * Если элемент имеет статус отличный от активированного,
         * значит элемент не был активирован и его не нужно деактивировать.
         */
        if (holder.getStatus() !== ReactElementStatus.ACTIVATED) {
            return Promise.resolve();
        }

        holder.setStatus(ReactElementStatus.PENDING);
        const tagName = this.element.tagName;
        this.log(LogCodes.DEACTIVATE_ATTEMPT, tagName);

        const children = getInnerReactBlocks(this.element);
        const deactivationChain: Promise<void | Error> =
            // получаем цепочку деактиваций всех дочерних react-элементов
            this.getOrderedAllReactChildren(
                children,
                (child) => this.deactivateChild(child),
            );

        const reactInitializer = this.reactInitializer;

        return deactivationChain
            .then(() =>
                ReactInitializer.onAppRenderComplete(this.element).then(() => {
                    this.log(LogCodes.DEACTIVATE_RENDER_COMPLETE, tagName);
                    reactInitializer.destroy();
                })
            )
            .then(() => {
                holder.setStatus(ReactElementStatus.DEACTIVATED);
                this.log(LogCodes.DEACTIVATE_SUCCESS, tagName);
                // вызываем все зарегистрированные обработчики.
                holder.fireHandlers(ReactLoaderHolderHandlerType.DEACTIVATE);
            })
            .catch((error) => {
                holder.setStatus(ReactElementStatus.ERROR);
                this.log(LogCodes.DEACTIVATE_ERROR, tagName);
                /**
                 * если какое-то из реакт-приложений не смогло деактивироваться,
                 * то обязательно вызываем обработчики, чтобы не стопить цепочку активаций.
                 */
                holder.fireHandlers(ReactLoaderHolderHandlerType.DEACTIVATE);
                throw error;
            });
    }

    private activateChild(child: HTMLElement): Promise<void> {
        /**
         * Чтобы ПРАВИЛЬНО активировать вложенное реакт приложение, его нужно активировать через хук активации скриптов.
         * Поэтому делаем промис, который зарезолвится, когда вложенное реакт приложение активируется.
         */
        return new Promise((resolve) => {
            const childHolder = getReactLoaderHolder(child);
            const handler: ReactLoaderHolderHandlerObject = {
                onAction: () => resolve(),
                onError: () => {},
            };
            /**
             * подписываемся на активацию реакт-приложения,
             * чтобы после нее зарезолвить промис и продолжить обработку других элементов в цепочке активаций
             */
            childHolder.addHandler(handler, ReactLoaderHolderHandlerType.ACTIVATE);

            // TODO: После эксперимента про новую активацию хуков, напрямую завязаться на вызов нового активатора (HookActivator)
            Capture.activate(child);
        });
    }

    private deactivateChild(child: HTMLElement): Promise<void> {
        /**
         * Чтобы ПРАВИЛЬНО деактивировать вложенное реакт приложение,
         * его нужно деактивировать через хук деактивации хуков.
         */
        return new Promise((resolve) => {
            const childHolder = getReactLoaderHolder(child);
            const handler: ReactLoaderHolderHandlerObject = {
                onAction: () => resolve(),
                onError: () => {},
            };
            /**
             * подписываемся на деактивацию реакт-приложения,
             * чтобы после нее зарезолвить промис и продолжить обработку других элементов в цепочке активаций
             */
            childHolder.addHandler(handler, ReactLoaderHolderHandlerType.DEACTIVATE);

            Capture.deactivate(child);
        });
    }

    private extractRequiredResources(element: HTMLElement): AssetsResources {
        const jsBundle = splitList(element.getAttribute('data-react')!);
        const startupJsBundles = splitList(element.getAttribute('data-startup-modules')!);
        const cssBundle = splitList(element.getAttribute('data-css')!);
        const l10nPackages: string[] = splitList(element.getAttribute('data-l10n')!);
        const cfgPackages: string[] = splitList(element.getAttribute('data-cfg')!);
        const requireJSModules: string[] = splitList(element.getAttribute('data-require-modules')!);

        return {
            jsBundle,
            startupJsBundles,
            cssBundle,
            l10nPackages,
            cfgPackages,
            requireJSModules,
        };
    }

    /**
     * Извлекает атрибуты с моделью данных для отрисовки.
     */
    private extractDataAttributes(element: HTMLElement): Record<string, string> {
        const attributes: Record<string, string> = {};

        if (element.hasAttributes()) {
            const attrs = element.attributes;

            for (let i = attrs.length - 1; i >= 0; i--) {
                attributes[attrs[i].name] = attrs[i].value;
            }

        }

        return attributes;
    }

    /**
     * Извлекает <ui-part/>-элементы.
     */
    private extractUIParts(element: HTMLElement): Record<string, string> {
        const uiParts: Record<string, string> = {};

        let resParts: HTMLElement[] = [];
        const templates: HTMLElement[] = [];

        // 1. Идем по детям. Если ребенок `<template>`, то добавляем парты из него в список.
        // 2. Если ребенок не `<template>`, смотрим что он сам `ui-part`, то берем его, иначе ищем в нем `ui-part`.
        element.childNodes.forEach(function (child: ChildNode) {
            // Извлекаем информацию только из элементов,
            // которые могут содержать вёрстку.
            if (child.nodeType !== Node.ELEMENT_NODE) {
                return;
            }

            let parts: Element[];
            const tagName = (child as HTMLElement).tagName.toLowerCase();

            if (child instanceof HTMLTemplateElement) {
                parts = Array.from(getPartsFromTemplate(child) || []);

                templates.push(child);
            } else {
                if (tagName === 'ui-part') {
                    parts = [child as Element];
                } else {
                    parts = Array.from(getPartsFromElement(element));
                }
            }

            resParts = resParts.concat(parts as HTMLElement[]);
        });

        resParts.forEach(part => {
            // Пропустим, если <template> будет содержать неожидаемое значение.
            if (part.tagName.toLowerCase() === 'ui-part') {
                const html = part.innerHTML;
                if (html) {
                    uiParts[part.getAttribute('data-part-id')!] = html;
                }
            }
        });

        // Удаляем теги `<template>`.
        templates.forEach(node => {
            element.removeChild(node);
        });

        return uiParts;
    }

    /**
     * Обработка всех дочерних react-элементов.
     * @return Вернет цепочку промисов в правильном порядке, где каждый промис вызывает функцию обработки.
     * Цепочка промисов необходима, чтобы дождаться активации каждого ребенка.
     * Порядок react-элементов: от дочерних к родительским элементам.
     */
    private getOrderedAllReactChildren(
        children: NodeListOf<HTMLElement>,
        handler: (child: HTMLElement) => Promise<void | Error> = () => Promise.resolve(),
    ): Promise<void | Error> {
        let result: Promise<void | Error> = Promise.resolve();

        // Идем с конца, так как сначала нужно обработать дочерние элементы.
        // querySelectorAll в getInnerReactBlocks ищет элементы в глубину,
        // это гарантирует, что дочерние элементы будут правее своих родителей в массиве.
        for (let i = children.length - 1; i >= 0; i--) {
            const node = children[i] as HTMLElement;
            // строим цепочку промисов
            result = result.then(() => handler(node));
        }

        return result;
    }

    private log(code: LogCodes, tagName: string, payload?: string) {
        logger.success(code, tagName, payload);
        OK.Tracer.log(`react-loader: code=${code}, tag=${tagName}${payload ? ", payload=" + payload : ""}`)
    }
}
/**
 * Разделяет строку по запятой и удаляет пустые элементы.
 */
function splitList(joinedList: string): string[] {
    const rawList: string[] = (joinedList || '').split(',');

    return rawList.reduce((list: string[], item) => {
        if (item) {
            list.push(item.trim());
        }

        return list;
    }, []);
}

function getPartsFromTemplate(template: HTMLTemplateElement): HTMLCollection {
    // Для старых браузеров у нас нет полифилла, поэтому там нет `.content`.
    const root = template.content || template;
    /*
     * Ожидается
     * <template>
     *     <ui-part data-part-id="foo">Foo</ui-part>
     *     <ui-part data-part-id="bar">Bar</ui-part>
     * </template>
     *
     * @see one.desktop.react.ReactTagRenderer::writeParts
     */
    return root.children;
}

function getPartsFromElement(element: HTMLElement): HTMLCollectionOf<Element> {
    return element.getElementsByTagName('ui-part');
}

/**
 * Найти все вложенные react-теги у элемента
 */
function getInnerReactBlocks(element: HTMLElement): NodeListOf<HTMLElement> {
    return element.querySelectorAll(REACT_ELEMENT_SELECTOR);
}
